import { Stack, styled, ThemeProvider, useTheme } from '@mui/material'
import { type Location, NavigateFunction, useLocation, useNavigate, useRouteError } from 'react-router-dom'
import {
    Button,
    ContactIconWithPopup,
    Footer,
    FullPageEmptyState1,
    FullPageEmptyState2,
    FullPageErrorImage,
    getStatusCode,
    HTTP_STATUS,
    sendToSentry,
    theme as defaultTheme,
    tokens,
    TOOLBAR_HEIGHT,
    Typography,
} from 'ComponentLibrary'
import { useEffect } from 'react'
import { signOut } from 'aws-amplify/auth'
import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { CaretLeftIcon, EnvelopeSimpleIcon, FileIcon, HouseIcon, SignOutIcon } from '@phosphor-icons/react'
import { ValuationApiError } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'
import { DEFAULT_LOCALE } from 'ClientPortal/src/i18n/supportedLocales'
import { i18n, initI18n } from 'ClientPortal/src/i18n/init'
import { useState } from 'react'
import { getCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { readPersistedLocale } from 'ClientPortal/src/i18n/persistedLocale'

type ErrorPageProps = { error: unknown; navigate: NavigateFunction; location: Location } & BaseErrorPageProps

type BaseErrorPageProps = {
    withToolbar?: boolean
}

export default function ErrorPage(props: BaseErrorPageProps) {
    const { isReady, error: localeError } = useInitialErrorPageLocale()
    const error = useRouteError()
    const navigate = useNavigate()
    const location = useLocation()

    if (localeError) {
        throw localeError
    }

    if (!isReady) {
        return null
    }

    return <BaseErrorPage {...props} error={error} navigate={navigate} location={location} />
}

// This is done to allow us to test this component with the current testing setup
// useRouteError is not available through our testing setup because we have to
// switch the router to createMemoryRouter
export function BaseErrorPage({ withToolbar, error, navigate, location }: ErrorPageProps) {
    const errorStatus = getStatusCode(error)
    const theme = useTheme()
    const { t } = useTranslation('pages', { keyPrefix: 'errorPage' })
    const { errorTitle, errorMessage } = getLocalizedError(errorStatus, t)

    useEffect(() => {
        sendToSentry(error)
    }, [error])

    const showEmptyStateInstead = error instanceof ValuationApiError && errorStatus === HTTP_STATUS.NotFound

    const getEmptyStateImage = () => {
        if (location.pathname.endsWith('/assets')) {
            return <StyledFullPageEmptyState2Image data-testid="emptyState2Image" preserveAspectRatio="xMaxYMax meet" />
        }

        if (location.pathname.endsWith('/analysis')) {
            return <StyledFullPageEmptyState2Image data-testid="emptyState2Image" preserveAspectRatio="xMaxYMax meet" />
        }

        return <StyledFullPageEmptyState1Image data-testid="emptyState1Image" preserveAspectRatio="xMaxYMax meet" />
    }

    const getEmptyStateMessage = () => {
        if (location.pathname.endsWith('/assets')) {
            return t('emptyState.assets')
        }

        if (location.pathname.endsWith('/analysis')) {
            return t('emptyState.analysis')
        }

        return t('emptyState.default')
    }

    const getActions = () => {
        if (showEmptyStateInstead) {
            return <Button startIcon={<FileIcon />} label={t('actions.orders')} onClick={() => navigate(STATIC_APP_PATHS.orderListPage)} />
        }

        if (!withToolbar && errorStatus !== 404) {
            // When the toolbar is missing there will be no user menu and navigation buttons anymore
            return <Button startIcon={<SignOutIcon />} label={t('actions.signOut')} onClick={() => signOut()} />
        }
        return (
            <>
                <Button startIcon={<CaretLeftIcon />} variant="secondary" label={t('actions.back')} onClick={() => navigate(-1)} />
                <Button startIcon={<HouseIcon />} label={t('actions.overview')} onClick={() => navigate('/')} />
            </>
        )
    }

    return (
        <ThemeProvider theme={defaultTheme}>
            <ErrorPageContainer $withToolbar={withToolbar}>
                <ErrorStack direction="column" spacing={theme.spacing(4)}>
                    <Typography variant="headline1">{showEmptyStateInstead ? t('emptyStateTitle') : errorTitle}</Typography>
                    <Typography variant="bodyTextLarge">{showEmptyStateInstead ? getEmptyStateMessage() : errorMessage}</Typography>
                    {!showEmptyStateInstead && (
                        <Stack direction="column" gap={theme.spacing(2)}>
                            <Typography variant="bodyTextLarge">{t('supportText')}</Typography>
                            <ContactIconWithPopup
                                icon={<EnvelopeSimpleIcon size={tokens.SizingIconM} />}
                                label="support@einwert.com"
                                value={'support@einwert.com'}
                                linkTo={`mailto:support@einwert.com`}
                                showTextButton
                            />
                        </Stack>
                    )}
                    <Stack direction="row" gap={theme.spacing(6)} sx={{ paddingTop: theme.spacing(8) }}>
                        {getActions()}
                    </Stack>
                </ErrorStack>
                {showEmptyStateInstead ? (
                    getEmptyStateImage()
                ) : (
                    <StyledErrorImage data-testid="errorImage" preserveAspectRatio="xMaxYMax meet" />
                )}
            </ErrorPageContainer>
            <Stack direction="row" position="absolute" bottom={0} width="100%" zIndex="2" sx={{ backgroundColor: theme.palette.white }}>
                <Footer />
            </Stack>
        </ThemeProvider>
    )
}

function getLocalizedError(statusCode: number | undefined, t: (key: string) => string) {
    switch (statusCode) {
        case HTTP_STATUS.Unauthorized:
            return {
                errorTitle: t('status.unauthorized.title'),
                errorMessage: t('status.unauthorized.message'),
            }
        case HTTP_STATUS.Forbidden:
            return {
                errorTitle: t('status.forbidden.title'),
                errorMessage: t('status.forbidden.message'),
            }
        case HTTP_STATUS.NotFound:
            return {
                errorTitle: t('status.notFound.title'),
                errorMessage: t('status.notFound.message'),
            }
        case undefined:
        default:
            return {
                errorTitle: t('status.default.title'),
                errorMessage: t('status.default.message'),
            }
    }
}

function useInitialErrorPageLocale() {
    const [isReady, setIsReady] = useState(i18n.isInitialized)
    const [error, setError] = useState<unknown>()

    useEffect(() => {
        const persistedLocale = readPersistedLocale()
        const fallbackLocale = i18n.isInitialized ? getCurrentResolvedLocale(i18n) : (persistedLocale ?? DEFAULT_LOCALE)

        if (i18n.isInitialized) {
            setIsReady(true)
            return
        }

        let isMounted = true

        void initI18n(fallbackLocale)
            .then(() => {
                if (isMounted) {
                    setError(undefined)
                    setIsReady(true)
                }
            })
            .catch((initializationError) => {
                if (isMounted) {
                    setError(initializationError)
                }
            })

        return () => {
            isMounted = false
        }
    }, [])

    return { error, isReady }
}

const ErrorPageContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $withToolbar }: { $withToolbar?: boolean }) => ({
    width: '100vw',
    height: $withToolbar ? `calc(calc(100vh - ${TOOLBAR_HEIGHT}) - 1px)` : '100vh',
    position: 'absolute',
    left: 0,
    top: $withToolbar ? `${TOOLBAR_HEIGHT}` : 0,
    overflow: 'hidden',
}))

const ErrorStack = styled(Stack)(({ theme }) => ({
    position: 'absolute',
    left: '18.5%',
    top: '18.5%',
    maxWidth: '480px',
    backgroundColor: theme.palette.white,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(4),
    zIndex: 2,
}))

const StyledErrorImage = styled(FullPageErrorImage)(() => ({
    width: '500%', //this is to allow the picture to overflow horizontally past the width of the container/screen
    height: '100%',
    position: 'absolute',
    right: 0,
    zIndex: 1,
}))

const StyledFullPageEmptyState1Image = styled(FullPageEmptyState1)(() => ({
    width: '500%', //this is to allow the picture to overflow horizontally past the width of the container/screen
    height: '100%',
    position: 'absolute',
    right: 0,
    zIndex: 1,
}))

const StyledFullPageEmptyState2Image = styled(FullPageEmptyState2)(() => ({
    width: '500%', //this is to allow the picture to overflow horizontally past the width of the container/screen
    height: '100%',
    position: 'absolute',
    padding: '120px',
    right: 0,
    zIndex: 1,
}))
